
import { defineComponent } from "vue";
import { suggestSocialCaption } from "@/api/ghlAi.api.js";
import { getPlatformMeta } from "@/utils/socialPlatformMeta.js";

type Draft = {
  text: string;
};

type DraftCountOption = {
  value: number;
  label: string;
  description: string;
};

const DRAFT_COUNT_OPTIONS: DraftCountOption[] = [
  { value: 1, label: "One caption", description: "Quick single suggestion" },
  { value: 2, label: "Two options", description: "Compare two different angles" },
  { value: 3, label: "Three options", description: "Most variety; uses more credits" }
];

export default defineComponent({
  name: "suggest-caption-dialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String as () => "draft" | "rewrite",
      default: "draft"
    },
    platforms: {
      type: Array as () => string[],
      default: () => []
    },
    existingCaption: {
      type: String,
      default: ""
    }
  },
  emits: ["update:modelValue", "select"],
  data() {
    return {
      generating: false,
      creditsError: "",
      creditsRemaining: null as number | null,
      drafts: [] as Draft[],
      draftCountOptions: DRAFT_COUNT_OPTIONS,
      form: {
        topic: "",
        intent: "",
        draftCount: 1
      }
    };
  },
  computed: {
    visible: {
      get(): boolean {
        return this.modelValue;
      },
      set(value: boolean) {
        this.$emit("update:modelValue", value);
      }
    },
    dialogTitle(): string {
      return this.mode === "rewrite" ? "Rewrite caption" : "Suggest caption";
    },
    platformLabels(): string[] {
      return (this.platforms || []).map((platform) => getPlatformMeta(platform).label);
    },
    resultsIntro(): string {
      const count = this.drafts.length;
      const noun = count === 1 ? "caption option" : "caption options";
      return `Here ${count === 1 ? "is your" : "are your"} ${count} ${noun}. Pick one to insert into your post — you can edit it afterward.`;
    }
  },
  watch: {
    modelValue(open: boolean) {
      if (open) {
        this.resetForOpen();
      }
    }
  },
  methods: {
    resetForOpen() {
      this.drafts = [];
      this.creditsError = "";
      this.creditsRemaining = null;
      this.form.intent = "";
      this.form.draftCount = 1;
      this.form.topic = this.mode === "rewrite"
        ? String(this.existingCaption || "").trim()
        : "";
    },
    close() {
      this.visible = false;
    },
    onClosed() {
      this.generating = false;
      this.drafts = [];
      this.creditsError = "";
    },
    async generate() {
      const topic = this.form.topic.trim();
      if (!topic || this.generating) return;

      this.generating = true;
      this.creditsError = "";

      try {
        const response = await suggestSocialCaption({
          topic,
          intent: this.form.intent.trim() || undefined,
          platforms: this.platforms,
          mode: this.mode,
          existingCaption: this.mode === "rewrite" ? this.existingCaption : undefined,
          draftCount: this.form.draftCount
        });
        const data = response.data || response;
        this.drafts = Array.isArray(data.drafts) ? data.drafts : [];
        this.creditsRemaining = Number(data.creditsRemaining) || null;

        if (!this.drafts.length) {
          this.creditsError = "No drafts were returned. Please try again.";
        }
      } catch (err) {
        const error = err as { response?: { status?: number; data?: { message?: string } }; message?: string };
        const status = error.response?.status;
        const message = error.response?.data?.message || error.message || "Unable to generate caption.";
        if (status === 429 || /insufficient ai credits/i.test(message)) {
          this.creditsError = "Insufficient AI credits.";
        } else {
          this.creditsError = message;
        }
      } finally {
        this.generating = false;
      }
    },
    regenerate() {
      this.drafts = [];
      this.generate();
    },
    useDraft(text: string) {
      this.$emit("select", text);
      this.close();
    }
  }
});
